import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { X, Camera, RefreshCw, Trash2, Plus, CheckCircle, Circle, MessageSquare } from 'lucide-react';
import { Task } from '../../types/jobcard';
import { deleteTask } from '../../services/jobcardApi';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import AddTaskEquipmentModal from './AddTaskEquipmentModal';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { compressImage, formatFileSize } from '../../utils/imageUtils';
import { getTaskImageUrl } from '../../config';
import { Store } from '../../types/store';
import { getStores } from '../../services/storeApi';
import { SopCompletion, getSopCompletion, updateSopStep, finalizeSopCompletion } from '../../services/sopApi';
import { useAuth } from '../../hooks/useAuth';
// import SelectEquipmentModal from './SelectEquipmentModal';

interface EditTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    task: Task;
    jobCardId: number;
    jobcardStoreNumber?: string;
}

interface SelectedEquipment {
    id: number;
    allocationType: 'store' | 'equipment';
    storeId?: number;
    allocatedId?: number;
    store_number?: string;
    allocationDate: string;
    allocationNotes?: string;
    name: string;
    serial_number?: string;
    quantity?: number;
    order_number?: string;
    equipment_type?: { 
        name: string;
        id?: number;
        subtypes?: { id: number; name: string }[];
    };
    equipment_subtype_id?: number;
    groupedIds?: number[];
}

interface UpdateTaskRequest {
    description: string;
    pictures?: File[];
    delete_pictures?: number[];
    equipment?: {
        equipment_id: number;
        allocation_type: 'store' | 'equipment';
        allocated_id?: number;
        store_id?: number;
        store_number?: string;
        allocation_date: string;
        allocation_notes?: string;
        quantity: number;
        order_number?: string;
        groupedIds?: number[];
    }[];
}

// Modify the updateTask function to correctly format equipment data with allocation information
const updateTask = async (jobCardId: number, taskId: number, data: UpdateTaskRequest) => {
    const formData = new FormData();
    
    // Add basic fields
    formData.append('description', data.description);
    
    // Add equipment as a properly formatted array for Laravel
    if (data.equipment && data.equipment.length > 0) {
        console.log(`Processing ${data.equipment.length} equipment items for submission`, data.equipment);
        
        // Instead of JSON.stringify, use array notation in field names
        data.equipment.forEach((eq, index) => {
            Object.entries(eq).forEach(([key, value]) => {
                // Skip undefined values
                if (value !== undefined && value !== null) {
                    // Special handling for groupedIds array
                    if (key === 'groupedIds' && Array.isArray(value)) {
                        // Append each groupedId individually to ensure Laravel receives it as an array
                        value.forEach((groupedId, groupedIndex) => {
                            formData.append(`equipment[${index}][groupedIds][${groupedIndex}]`, groupedId.toString());
                            console.log(`Added equipment[${index}][groupedIds][${groupedIndex}] = ${groupedId}`);
                        });
                    } else {
                        // Convert any objects to JSON strings to avoid FormData issues
                        const formattedValue = typeof value === 'object' ? JSON.stringify(value) : value.toString();
                        formData.append(`equipment[${index}][${key}]`, formattedValue);
                        console.log(`Added equipment[${index}][${key}] = ${typeof value === 'object' ? '[Object]' : value}`);
                    }
                }
            });
            
            // Ensure allocation type is correctly set
            if (!formData.has(`equipment[${index}][allocation_type]`)) {
                formData.append(`equipment[${index}][allocation_type]`, 'store');
                console.log(`Added default equipment[${index}][allocation_type] = store`);
            }
            
            // Ensure store_number is included for store allocation
            if (eq.allocation_type === 'store' && eq.store_number && !formData.has(`equipment[${index}][store_number]`)) {
                formData.append(`equipment[${index}][store_number]`, eq.store_number);
                console.log(`Added equipment[${index}][store_number] = ${eq.store_number}`);
            }
        });
    } else {
        // If no equipment, send an empty array in a way Laravel understands
        // Use a special notation that Laravel will recognize as an empty array
        formData.append('equipment', '');
        console.log('No equipment to submit - sending empty equipment field');
    }
    
    // Add pictures to delete
    if (data.delete_pictures && data.delete_pictures.length > 0) {
        data.delete_pictures.forEach(id => {
            formData.append('delete_pictures[]', id.toString());
            console.log(`Added delete_pictures[] = ${id}`);
        });
    }
    
    // Add new pictures
    if (data.pictures && data.pictures.length > 0) {
        data.pictures.forEach(file => {
            formData.append('pictures[]', file);
            console.log(`Added picture: ${file.name}`);
        });
    }
    
    // Make the API call with detailed error logging
    try {
        // Log entire FormData for debugging
        for (const pair of formData.entries()) {
            console.log(`FormData field: ${pair[0]} = ${pair[1]}`);
        }
        
        // Use the direct POST endpoint - no need for method spoofing
        console.log(`Sending POST request to /jobcards/${jobCardId}/tasks/${taskId}`);
        const response = await api.post(`/jobcards/${jobCardId}/tasks/${taskId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        console.log('API response:', response);
        return response;
    } catch (error: any) {
        console.error('API call failed:', error);
        if (error.response?.data) {
            console.error('Full error response:', JSON.stringify(error.response.data, null, 2));
        }
        throw error;
    }
};

export default function EditTaskModal({ isOpen, onClose, onSuccess, task, jobCardId, jobcardStoreNumber }: EditTaskModalProps) {
    const { user } = useAuth();
    const [description, setDescription] = useState(task.description);
    const [newPictures, setNewPictures] = useState<File[]>([]);
    const [picturesToDelete, setPicturesToDelete] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showEquipmentModal, setShowEquipmentModal] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState<SelectedEquipment[]>([]);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [stores, setStores] = useState<Store[]>([]);
    
    // SOP related state
    const [sopCompletion, setSopCompletion] = useState<SopCompletion | null>(null);
    const [isLoadingSop, setIsLoadingSop] = useState(false);
    const isSopTask = task.task_type === 'sop' && task.sop_completion_id;
    
    // Check if this is an ATR-generated task
    const isATRTask = task.description.endsWith(' resolved');

    // Debug the jobcardStoreNumber prop
    // console.log('EditTaskModal - Props received - jobcardStoreNumber:', jobcardStoreNumber, 'from jobCard.store.storenumber');

    useEffect(() => {
        if (isOpen) {
            setDescription(task.description);
            setNewPictures([]);
            setPicturesToDelete([]);
            setError(null);
            
            // Fetch stores for store allocation
            const fetchStores = async () => {
                try {
                    const response = await getStores();
                    setStores(response.data.data || []);
                } catch (error) {
                    console.error('Failed to fetch stores:', error);
                    toast.error('Failed to load stores');
                }
            };
            
            fetchStores();
            
            // Load SOP completion if this is a SOP task
            if (isSopTask && task.sop_completion_id) {
                const fetchSopCompletion = async () => {
                    try {
                        setIsLoadingSop(true);
                        const response = await getSopCompletion(task.sop_completion_id!);
                        setSopCompletion(response.data);
                    } catch (error) {
                        console.error('Failed to fetch SOP completion:', error);
                        toast.error('Failed to load SOP completion data');
                    } finally {
                        setIsLoadingSop(false);
                    }
                };
                fetchSopCompletion();
            }
            
            // Debug jobcardStoreNumber in useEffect when modal opens
            // console.log('EditTaskModal useEffect when opened - jobcardStoreNumber:', jobcardStoreNumber);
            
            // Convert existing task equipment to the request format
            if (task.equipment) {
                console.log('Initializing from existing task equipment:', task.equipment);
                setSelectedEquipment(
                    task.equipment.map(eq => {
                        // Check if eq has a groupedIds property using type assertion
                        const hasGroupedIds = 'groupedIds' in (eq as any);
                        
                        const item = {
                            id: eq.equipment_id,
                            allocationType: eq.allocation_type || 'store',
                            allocatedId: eq.allocated_id,
                            // Use type assertion to access store_id safely
                            storeId: (eq as any).store_id,
                            store_number: eq.store_number,
                            allocationDate: eq.allocation_date || new Date().toISOString().split('T')[0],
                            allocationNotes: eq.allocation_notes || '',
                            name: eq.equipment?.name || '',
                            serial_number: eq.equipment?.serial_number,
                            quantity: eq.quantity || 1,
                            order_number: eq.order_number || '',
                            equipment_type: eq.equipment?.equipment_type && {
                                name: eq.equipment.equipment_type.name,
                                id: eq.equipment.equipment_type.id,
                                subtypes: eq.equipment.equipment_type.subtypes
                            },
                            equipment_subtype_id: eq.equipment?.equipment_subtype_id,
                            // Use optional chaining and type assertion to safely access groupedIds
                            groupedIds: hasGroupedIds ? (eq as any).groupedIds : undefined
                        };
                        console.log('Converted equipment item:', item);
                        return item;
                    })
                );
            } else {
                setSelectedEquipment([]);
            }
        }
    }, [isOpen, task, jobcardStoreNumber]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        try {
            const compressedFiles: File[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                if (!file.type.startsWith('image/')) {
                    showErrorToast(`${file.name} is not an image file`);
                    continue;
                }

                const originalSize = formatFileSize(file.size);
                const compressedFile = await compressImage(file);
                const compressedSize = formatFileSize(compressedFile.size);
                
                console.log(`Compressed ${file.name}: ${originalSize} → ${compressedSize}`);
                compressedFiles.push(compressedFile);
            }

            setNewPictures(prev => [...prev, ...compressedFiles]);
        } catch (error) {
            console.error('Error processing images:', error);
            showErrorToast('Failed to process images');
        }
    };

    const handleCameraCapture = async () => {
        if (!videoRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Draw the video frame to the canvas
        ctx.drawImage(videoRef.current, 0, 0);

        // Convert the canvas to a blob
        canvas.toBlob(async (blob) => {
            if (!blob) return;

            // Create a File object from the blob
            const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
            setNewPictures(prev => [...prev, file]);

            // Stop the camera stream
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }
            setShowCamera(false);
        }, 'image/jpeg');
    };

    const removeNewPicture = (index: number) => {
        setNewPictures(prev => prev.filter((_, i) => i !== index));
    };

    const togglePictureToDelete = (pictureId: number) => {
        setPicturesToDelete(prev => {
            if (prev.includes(pictureId)) {
                return prev.filter(id => id !== pictureId);
            }
            return [...prev, pictureId];
        });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null); // Clear any previous errors

        try {
            // For ATR tasks, only allow picture editing
            if (isATRTask) {
                // Check if there are any picture changes
                if (newPictures.length === 0 && picturesToDelete.length === 0) {
                    setError("No changes to save. ATR tasks can only have their pictures modified.");
                    setIsSubmitting(false);
                    return;
                }
            }

            // Check if description is empty (only for non-ATR tasks)
            if (!isATRTask && !description.trim()) {
                setError("Task description cannot be empty");
                setIsSubmitting(false);
                return;
            }

            // Create a proper equipment list that preserves quantities and allocation info (only for non-ATR tasks)
            const equipmentList = !isATRTask && selectedEquipment.length > 0 
                ? selectedEquipment.map(eq => {
                    // Ensure quantity is at least 1
                    const equipmentQuantity = eq.quantity && eq.quantity > 0 ? eq.quantity : 1;
                    
                    const equipmentData = {
                        equipment_id: eq.id,
                        allocation_type: eq.allocationType || 'store',
                        allocated_id: eq.allocationType === 'equipment' ? eq.allocatedId : undefined,
                        store_id: eq.allocationType === 'store' ? eq.storeId : undefined,
                        store_number: eq.allocationType === 'store' ? eq.store_number : undefined,
                        allocation_date: eq.allocationDate || new Date().toISOString().split('T')[0],
                        allocation_notes: eq.allocationNotes || '',
                        quantity: equipmentQuantity,
                        order_number: eq.order_number || '',
                        groupedIds: eq.groupedIds // Include groupedIds if available
                    };
                    
                    console.log('Preparing equipment item for submission:', equipmentData);
                    return equipmentData;
                }) 
                : []; // Always return an empty array when no equipment is selected

            const taskData: UpdateTaskRequest = {
                description: isATRTask ? task.description : description, // Keep original description for ATR tasks
                pictures: newPictures,
                delete_pictures: picturesToDelete,
                equipment: isATRTask ? undefined : equipmentList  // No equipment changes for ATR tasks
            };

            console.log('Sending task update with data:', JSON.stringify(taskData, null, 2));
            
            try {
                await updateTask(jobCardId, task.id, taskData);
                // console.log('Task updated successfully');
                showSuccessToast(isATRTask ? 'Pictures updated successfully' : 'Task updated successfully');
                onSuccess();
                onClose();
            } catch (apiError: any) {
                console.error('API Error:', apiError);
                
                // Check if there's a structured error response
                if (apiError.response?.data) {
                    console.error('API Error Response:', apiError.response.data);
                    
                    if (apiError.response.data.errors) {
                        // Format validation errors
                        const errorMessages = Object.values(apiError.response.data.errors)
                            .flat()
                            .join(', ');
                        setError(errorMessages || 'Validation failed');
                    } else if (apiError.response.data.message) {
                        setError(apiError.response.data.message);
                    } else {
                        setError('Failed to update task: Server error');
                    }
                } else {
                    setError(`Failed to update task: ${apiError.message || 'Unknown error'}`);
                }
                
                throw apiError; // Re-throw to stop execution
            }
        } catch (error) {
            console.error('Error updating task:', error);
            // Error is already set in the catch block above
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this task?')) {
            return;
        }

        try {
            setIsSubmitting(true);
            await deleteTask(jobCardId, task.id);
            onSuccess();
            onClose();
        } catch (err) {
            setError('Failed to delete task');
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const startCamera = async () => {
        try {
            // Stop any existing stream first
            if (videoRef.current?.srcObject) {
                const stream = videoRef.current.srcObject as MediaStream;
                stream.getTracks().forEach(track => track.stop());
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { exact: 'user' },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    aspectRatio: { ideal: 16 / 9 }
                }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.style.transform = 'none';
                videoRef.current.style.width = '100%';
                videoRef.current.style.height = 'auto';
                videoRef.current.style.maxHeight = '100vh';
                videoRef.current.style.objectFit = 'cover';
                videoRef.current.style.maxWidth = '100vw';

                await videoRef.current.play();
            }
            setShowCamera(true);
        } catch (err) {
            console.error('Error accessing camera:', err);
            // If exact facing mode fails, try without exact constraint
            try {
                const fallbackStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'user',
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        aspectRatio: { ideal: 16 / 9 }
                    }
                });

                if (videoRef.current) {
                    videoRef.current.srcObject = fallbackStream;
                    videoRef.current.style.transform = 'none';
                    videoRef.current.style.width = '100%';
                    videoRef.current.style.height = 'auto';
                    videoRef.current.style.maxHeight = '100vh';
                    videoRef.current.style.objectFit = 'cover';
                    videoRef.current.style.maxWidth = '100vw';

                    await videoRef.current.play();
                }
                setShowCamera(true);
            } catch (fallbackErr) {
                console.error('Fallback camera access failed:', fallbackErr);
                toast.error('Failed to access camera. Please check your permissions.');
            }
        }
    };

    useEffect(() => {
        if (showCamera) {
            startCamera();
        }
    }, [showCamera]);

    // Handle adding equipment from the AddTaskEquipmentModal - similar to CreateTaskModal
    const handleAddEquipment = (selectedItems: { 
        id: number; 
        quantity: number; 
        order_number?: string;
        name?: string;
        serial_number?: string;
        equipment_type?: {
            name: string;
            id?: number;
        };
        equipment_subtype_id?: number;
        groupedIds?: number[];
    }[]) => {
        // Debug the jobcardStoreNumber in handleAddEquipment
        console.log('handleAddEquipment - jobcardStoreNumber:', jobcardStoreNumber);
        console.log('handleAddEquipment - stores:', stores);
        console.log('Raw selected items:', JSON.stringify(selectedItems, null, 2));

        // Process each item individually to ensure unique IDs
        const newEquipment = selectedItems.map(item => {
            // For non-grouped items or items without groupedIds
            if (!item.groupedIds || item.groupedIds.length === 0) {
                console.log(`Processing direct item: ${item.name} (ID: ${item.id})`);
                // Find matching store based on jobcardStoreNumber
                const matchingStore = stores.find(s => s.storenumber === jobcardStoreNumber);
                console.log('Matching store for', jobcardStoreNumber, ':', matchingStore);
                
                return {
                    id: item.id,
                    name: item.name || '',
                    serial_number: item.serial_number,
                    equipment_type: item.equipment_type,
                    equipment_subtype_id: item.equipment_subtype_id,
                    order_number: item.order_number,
                    allocationType: 'store' as const,
                    storeId: matchingStore?.id,
                    store_number: jobcardStoreNumber,
                    allocationDate: new Date().toISOString().split('T')[0],
                    quantity: item.quantity || 1
                };
            }
            
            // This is a grouped item
            console.log(`Processing grouped item: ${item.name} (ID: ${item.id}) with ${item.groupedIds.length} items`);
            const matchingStore = stores.find(s => s.storenumber === jobcardStoreNumber);
            console.log('Matching store for', jobcardStoreNumber, ':', matchingStore);
            
            return {
                id: item.id,
                name: item.name || '',
                serial_number: item.serial_number,
                equipment_type: item.equipment_type,
                equipment_subtype_id: item.equipment_subtype_id,
                order_number: item.order_number,
                allocationType: 'store' as const,
                storeId: matchingStore?.id,
                store_number: jobcardStoreNumber,
                allocationDate: new Date().toISOString().split('T')[0],
                quantity: item.quantity || 1,
                groupedIds: item.groupedIds
            };
        });

        console.log('New equipment to add:', JSON.stringify(newEquipment, null, 2));
        
        // Add to existing equipment, preventing duplicates
        setSelectedEquipment(prev => {
            const uniqueEquipmentMap = new Map();
            [...prev, ...newEquipment].forEach(item => {
                if (!uniqueEquipmentMap.has(item.id)) {
                    uniqueEquipmentMap.set(item.id, item);
                }
            });
            return Array.from(uniqueEquipmentMap.values());
        });
        
        setShowEquipmentModal(false);
    };

    // SOP step handling functions
    const updateSopStepCompletion = async (stepId: number, isCompleted: boolean, comment?: string) => {
        if (!sopCompletion) return;

        try {
            const updatedSteps = sopCompletion.completion_steps.map(step => 
                step.id === stepId 
                    ? { ...step, is_completed: isCompleted, comment: comment || step.comment }
                    : step
            );

            setSopCompletion({
                ...sopCompletion,
                completion_steps: updatedSteps
            });

            await updateSopStep(stepId, { is_completed: isCompleted, comment });
            
        } catch (error) {
            console.error('Error updating SOP step:', error);
            toast.error('Failed to update step');
            
            // Revert the change on error
            if (task.sop_completion_id) {
                const response = await getSopCompletion(task.sop_completion_id);
                setSopCompletion(response.data);
            }
        }
    };

    const handleSopStepToggle = (stepId: number, currentStatus: boolean, comment?: string) => {
        updateSopStepCompletion(stepId, !currentStatus, comment);
    };

    const handleSopCommentChange = (stepId: number, isCompleted: boolean, comment: string) => {
        updateSopStepCompletion(stepId, isCompleted, comment);
    };

    const handleFinalizeSop = async () => {
        if (!sopCompletion || !user) return;

        const requiredSteps = sopCompletion.sop.steps.filter(s => s.is_required);
        const completedRequiredSteps = sopCompletion.completion_steps.filter(cs => 
            cs.is_completed && sopCompletion.sop.steps.find(s => s.id === cs.step_id)?.is_required
        );

        if (requiredSteps.length !== completedRequiredSteps.length) {
            toast.error('All required steps must be completed before finalizing');
            return;
        }

        try {
            await finalizeSopCompletion(sopCompletion.id, {
                technician_name: user.name || user.email
            });

            toast.success('SOP completed successfully!');
            
            // Refresh the SOP completion data
            if (task.sop_completion_id) {
                const response = await getSopCompletion(task.sop_completion_id);
                setSopCompletion(response.data);
            }
            
        } catch (error: any) {
            console.error('Error finalizing SOP:', error);
            toast.error(error.response?.data?.message || 'Failed to finalize SOP');
        }
    };

    if (showCamera) {
        return (
            <div className="fixed inset-0 bg-black dark:bg-gray-900 z-50">
                <div className="relative h-full">
                    <video
                        ref={videoRef}
                        className="w-full h-full object-cover"
                        autoPlay
                        playsInline
                        muted
                    />
                    <div className="absolute bottom-8 inset-x-0 flex justify-center space-x-4 px-4">
                        <button
                            type="button"
                            onClick={startCamera}
                            className="p-4 bg-white/20 dark:bg-gray-800/40 backdrop-blur-sm rounded-full shadow-lg hover:bg-white/30 dark:hover:bg-gray-800/60 transition-colors duration-200"
                        >
                            <RefreshCw size={24} className="text-white dark:text-gray-200" />
                        </button>
                        <button
                            type="button"
                            onClick={handleCameraCapture}
                            className="p-6 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <Camera size={32} className="text-gray-900 dark:text-gray-200" />
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            const stream = videoRef.current?.srcObject as MediaStream;
                            if (stream) {
                                stream.getTracks().forEach(track => track.stop());
                            }
                            setShowCamera(false);
                        }}
                        className="absolute top-4 left-4 p-4 text-white dark:text-gray-200 hover:text-gray-200 dark:hover:text-gray-300 transition-colors duration-200"
                    >
                        <X size={32} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 overflow-y-auto"
        >
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 bg-black/50" aria-hidden="true" />

                <div className="relative w-full max-w-3xl mx-auto my-8">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
                        {/* Header - Fixed */}
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center">
                                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Edit Task
                                </Dialog.Title>
                                <button
                                    onClick={onClose}
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Content - Scrollable */}
                        <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
                            {error && (
                                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                                    <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                                </div>
                            )}

                            <form id="edit-task-form" onSubmit={handleSubmit} className="space-y-6">
                                {/* Description Field */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Description *
                                        {isATRTask && (
                                            <span className="ml-2 text-xs text-amber-600 dark:text-amber-400 font-normal">
                                                (ATR-generated task - cannot be edited)
                                            </span>
                                        )}
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={isATRTask}
                                        className={`w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none ${
                                            isATRTask ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed opacity-75' : ''
                                        }`}
                                        rows={4}
                                        required
                                    />
                                </div>

                                {/* Equipment Section - Hidden for ATR tasks */}
                                {!isATRTask && (
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Equipment
                                            </label>
                                            <div className="flex items-center gap-2">
                                                {selectedEquipment.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedEquipment([])}
                                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors duration-200"
                                                    >
                                                        Clear All
                                                    </button>
                                                )}
                                            <button
                                                type="button"
                                                onClick={() => setShowEquipmentModal(true)}
                                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors duration-200"
                                            >
                                                <Plus size={16} className="mr-1.5" />
                                                Add Equipment
                                            </button>
                                            </div>
                                        </div>

                                    <div className="h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 -mr-2">
                                        {selectedEquipment.length > 0 ? (
                                            <div className="space-y-4">
                                                {(() => {
                                                    // Group equipment by type, subtype
                                                    const groupedEquipment = selectedEquipment.reduce((acc, item) => {
                                                        const typeKey = item.equipment_type?.name || 'Unknown Type';
                                                        const subtypeKey = item.equipment_type?.subtypes?.find(
                                                            (s: { id: number }) => s.id === item.equipment_subtype_id
                                                        )?.name || undefined;
                                                        
                                                        const key = `${typeKey}${subtypeKey ? `:${subtypeKey}` : ''}`;
                                                        
                                                        if (!acc[key]) {
                                                            acc[key] = {
                                                                groups: {},
                                                                type: typeKey,
                                                                subtype: subtypeKey
                                                            };
                                                        }
                                                        
                                                        // Further group by model, order number, and allocation
                                                        const modelKey = `${item.name || ''}|${item.serial_number || ''}|${item.order_number || ''}|${item.allocationType || ''}|${item.store_number || ''}|${item.allocatedId || ''}`;
                                                        
                                                        if (!acc[key].groups[modelKey]) {
                                                            acc[key].groups[modelKey] = {
                                                                item: {...item},
                                                                count: 0
                                                            };
                                                        }
                                                        
                                                        // Increment count
                                                        acc[key].groups[modelKey].count += (item.quantity || 1);
                                                        
                                                        return acc;
                                                    }, {} as Record<string, {
                                                        groups: Record<string, {
                                                            item: SelectedEquipment,
                                                            count: number
                                                        }>,
                                                        type: string,
                                                        subtype?: string
                                                    }>);
                                                    
                                                    return Object.entries(groupedEquipment).map(([key, group]) => (
                                                        <div key={key} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                            <div className="bg-gray-100 dark:bg-gray-700 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                {group.type}{group.subtype ? ` > ${group.subtype}` : ''}
                                                            </div>
                                                            <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                                {Object.values(group.groups).map((groupItem, idx) => (
                                                                    <div key={idx} className="flex justify-between items-start p-3 bg-white dark:bg-gray-800">
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <span className="font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs">
                                                                                    {groupItem.count}x
                                                                                </span>
                                                                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                                    {groupItem.item.name}
                                                                                </p>
                                                                                {groupItem.item.order_number && (
                                                                                    <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                                                                                        Order #{groupItem.item.order_number}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                                {groupItem.item.serial_number && `S/N: ${groupItem.item.serial_number} • `}
                                                                                {groupItem.item.allocationType === 'store'
                                                                                    ? `Store: ${groupItem.item.store_number}`
                                                                                    : `Equipment #${groupItem.item.allocatedId}`}
                                                                            </p>
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                // Update the way we delete equipment - remove ALL items matching this criteria
                                                                                const modelKey = `${groupItem.item.name || ''}|${groupItem.item.serial_number || ''}|${groupItem.item.order_number || ''}|${groupItem.item.allocationType || ''}|${groupItem.item.store_number || ''}|${groupItem.item.allocatedId || ''}`;
                                                                                
                                                                                // Filter out all items matching this model key
                                                                                setSelectedEquipment(prev => 
                                                                                    prev.filter(item => {
                                                                                        const itemKey = `${item.name || ''}|${item.serial_number || ''}|${item.order_number || ''}|${item.allocationType || ''}|${item.store_number || ''}|${item.allocatedId || ''}`;
                                                                                        return itemKey !== modelKey;
                                                                                    })
                                                                                );
                                                                            }}
                                                                            className="ml-3 p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                                                                        >
                                                                            <Trash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-full bg-white dark:bg-gray-800 rounded-md">
                                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                                    No equipment added yet
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                )}

                                {/* SOP Steps Section - Only show for SOP tasks */}
                                {isSopTask && sopCompletion && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                                                SOP Steps: {sopCompletion.sop.title}
                                            </h3>
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                                sopCompletion.status === 'completed' 
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                            }`}>
                                                {sopCompletion.status === 'completed' ? 'Completed' : 'In Progress'}
                                            </span>
                                        </div>
                                        
                                        {/* Progress indicator */}
                                        {(() => {
                                            const requiredSteps = sopCompletion.sop.steps.filter(s => s.is_required);
                                            const completedRequired = sopCompletion.completion_steps.filter(cs => 
                                                cs.is_completed && sopCompletion.sop.steps.find(s => s.id === cs.step_id)?.is_required
                                            );
                                            return (
                                                <div className="mb-4 text-sm text-blue-800 dark:text-blue-200">
                                                    Progress: {completedRequired.length}/{requiredSteps.length} required steps completed
                                                </div>
                                            );
                                        })()}
                                        
                                        {/* Steps list */}
                                        <div className="space-y-3 max-h-60 overflow-y-auto">
                                            {sopCompletion.sop.steps.map((sopStep) => {
                                                const completionStep = sopCompletion.completion_steps.find(cs => cs.step_id === sopStep.id);
                                                if (!completionStep) return null;

                                                return (
                                                    <div 
                                                        key={sopStep.id}
                                                        className={`border rounded-lg p-3 ${
                                                            completionStep.is_completed
                                                                ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                                                                : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSopStepToggle(completionStep.id, completionStep.is_completed, completionStep.comment)}
                                                                className="mt-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                                                                disabled={sopCompletion.status === 'completed'}
                                                            >
                                                                {completionStep.is_completed ? (
                                                                    <CheckCircle size={20} className="text-green-600 dark:text-green-400" />
                                                                ) : (
                                                                    <Circle size={20} />
                                                                )}
                                                            </button>
                                                            
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                        Step {sopStep.step_order}
                                                                    </span>
                                                                    {sopStep.is_required && (
                                                                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                                                            Required
                                                                        </span>
                                                                    )}
                                                                    {sopStep.requires_comment && (
                                                                        <MessageSquare size={14} className="text-blue-500" />
                                                                    )}
                                                                </div>
                                                                
                                                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                                                    {sopStep.step_text}
                                                                </p>
                                                                
                                                                {sopStep.instructions && (
                                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                                                        {sopStep.instructions}
                                                                    </p>
                                                                )}
                                                                
                                                                {(sopStep.requires_comment || completionStep.comment) && (
                                                                    <textarea
                                                                        value={completionStep.comment || ''}
                                                                        onChange={(e) => handleSopCommentChange(completionStep.id, completionStep.is_completed, e.target.value)}
                                                                        placeholder={sopStep.requires_comment ? 'Comment required...' : 'Add comment...'}
                                                                        className="w-full text-xs rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                                                                        rows={2}
                                                                        disabled={sopCompletion.status === 'completed'}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        
                                        {/* Finalize button */}
                                        {sopCompletion.status === 'in_progress' && (() => {
                                            const requiredSteps = sopCompletion.sop.steps.filter(s => s.is_required);
                                            const completedRequired = sopCompletion.completion_steps.filter(cs => 
                                                cs.is_completed && sopCompletion.sop.steps.find(s => s.id === cs.step_id)?.is_required
                                            );
                                            const canFinalize = requiredSteps.length === completedRequired.length;
                                            
                                            return (
                                                <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-700">
                                                    <button
                                                        type="button"
                                                        onClick={handleFinalizeSop}
                                                        disabled={!canFinalize}
                                                        className={`w-full px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                                            canFinalize 
                                                                ? 'text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600'
                                                                : 'text-gray-400 bg-gray-200 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                                                        }`}
                                                    >
                                                        {canFinalize ? 'Complete SOP' : `Complete ${completedRequired.length}/${requiredSteps.length} required steps first`}
                                                    </button>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}

                                {/* SOP Loading State */}
                                {isSopTask && isLoadingSop && (
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <div className="flex items-center justify-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading SOP steps...</span>
                                        </div>
                                    </div>
                                )}

                                {/* Pictures Section */}
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                                        Pictures
                                    </label>
                                    
                                    {/* Existing Pictures */}
                                    {task.pictures && task.pictures.length > 0 && (
                                        <div className="mb-4">
                                            <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                                                {task.pictures.map((pic) => (
                                                    <div key={pic.id} className="relative flex-none w-48">
                                                        <div className="relative group">
                                                            <img
                                                                src={getTaskImageUrl(pic.picture)}
                                                                alt="Task"
                                                                className={`w-full h-32 object-cover rounded-lg ${
                                                                    picturesToDelete.includes(pic.id) ? 'opacity-50' : ''
                                                                }`}
                                                                onError={(e) => {
                                                                    console.error(`Failed to load image: ${pic.picture}`, e);
                                                                }}
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-200 rounded-lg" />
                                                            <button
                                                                type="button"
                                                                onClick={() => togglePictureToDelete(pic.id)}
                                                                className={`absolute top-2 right-2 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 ${
                                                                    picturesToDelete.includes(pic.id)
                                                                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                                                                        : 'bg-red-100 text-red-600 hover:bg-red-200'
                                                                }`}
                                                            >
                                                                {picturesToDelete.includes(pic.id) ? (
                                                                    <Plus size={16} />
                                                                ) : (
                                                                    <X size={16} />
                                                                )}
                                                            </button>
                                                        </div>
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {pic.picture}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* New Pictures */}
                                    {newPictures.length > 0 && (
                                        <div className="mb-4">
                                            <div className="flex overflow-x-auto pb-4 space-x-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                                                {newPictures.map((pic, index) => (
                                                    <div key={index} className="relative flex-none w-48">
                                                        <div className="relative group">
                                                            <img
                                                                src={URL.createObjectURL(pic)}
                                                                alt={`New Task ${index + 1}`}
                                                                className="w-full h-32 object-cover rounded-lg"
                                                            />
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-200 rounded-lg" />
                                                            <button
                                                                type="button"
                                                                onClick={() => removeNewPicture(index)}
                                                                className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-200 transition-all duration-200"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>
                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {pic.name}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Picture Upload Buttons */}
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={startCamera}
                                            className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                                        >
                                            <Camera size={16} className="mr-2" />
                                            Take Picture
                                        </button>
                                        <label className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 cursor-pointer">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="hidden"
                                                multiple
                                            />
                                            <Plus size={16} className="mr-2" />
                                            Upload Pictures
                                        </label>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer - Fixed */}
                        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex justify-between">
                            {!isATRTask && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20 rounded-md hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors duration-200"
                                >
                                    Delete Task
                                </button>
                            )}
                            {isATRTask && <div></div>}
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="edit-task-form"
                                        disabled={isSubmitting}
                                        className="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors duration-200 bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title={isATRTask ? 'ATR task - only pictures can be edited' : ''}
                                    >
                                        {isSubmitting ? 'Saving...' : (isATRTask ? 'Save Pictures' : 'Save Changes')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AddTaskEquipmentModal
                isOpen={showEquipmentModal}
                onClose={() => setShowEquipmentModal(false)}
                onAdd={handleAddEquipment}
                existingEquipmentIds={selectedEquipment.map(eq => eq.id)}
                jobcardStoreNumber={jobcardStoreNumber}
            />
        </Dialog>
    );
} 